import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    CalendarDays,
    CheckCircle2,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Minus,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Target,
    Lightbulb,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

type Period = 'week' | 'month';

export default function ReportsPage() {
    const { t } = useTranslation();
    usePageTitle('reports.title');
    const { workspace } = useAuthStore();

    const [period, setPeriod] = useState<Period>('week');
    const [currentDate, setCurrentDate] = useState(new Date());

    const dateParam = currentDate.toISOString().split('T')[0];

    const { data, isLoading } = useQuery({
        queryKey: ['reports', workspace?.id, period, dateParam],
        queryFn: () => api.getReports(workspace!.id, period, dateParam),
        enabled: !!workspace?.id,
    });

    const navigatePeriod = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentDate);
        if (period === 'week') {
            newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        } else {
            newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        }
        setCurrentDate(newDate);
    };

    const formatPeriodLabel = () => {
        const report = data?.data;
        if (!report) return '';

        const start = new Date(report.period_start);
        const end = new Date(report.period_end);

        if (period === 'week') {
            return `${start.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })} - ${end.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
        }
        return start.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const report = data?.data;
    const summary = report?.summary;
    const changeVsPrevious = summary?.change_vs_previous;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">{t('reports.title', 'Báo cáo')}</h1>
                    <p className="text-neutral-600 dark:text-neutral-400">
                        {t('reports.overview', 'Theo dõi tiến độ công việc theo thời gian')}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Period Toggle */}
                    <div className="flex bg-neutral-100 dark:bg-neutral-700 rounded-lg p-1">
                        <button
                            onClick={() => setPeriod('week')}
                            className={clsx(
                                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                                period === 'week'
                                    ? 'bg-white dark:bg-neutral-600 shadow-sm'
                                    : 'text-neutral-600 dark:text-neutral-400'
                            )}
                        >
                            {t('reports.thisWeek', 'Tuần')}
                        </button>
                        <button
                            onClick={() => setPeriod('month')}
                            className={clsx(
                                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                                period === 'month'
                                    ? 'bg-white dark:bg-neutral-600 shadow-sm'
                                    : 'text-neutral-600 dark:text-neutral-400'
                            )}
                        >
                            {t('reports.thisMonth', 'Tháng')}
                        </button>
                    </div>

                    {/* Period Navigator */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigatePeriod('prev')}
                            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm font-medium min-w-[140px] text-center">
                            {formatPeriodLabel()}
                        </span>
                        <button
                            onClick={() => navigatePeriod('next')}
                            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                    title={t('reports.completed', 'Hoàn thành')}
                    value={summary?.completed || 0}
                    subtitle={`/ ${summary?.total_tasks || 0} tasks`}
                    icon={CheckCircle2}
                    color="success"
                />
                <SummaryCard
                    title={t('reports.completionRate', 'Tỷ lệ')}
                    value={`${summary?.completion_rate || 0}%`}
                    subtitle={t('reports.completed', 'hoàn thành')}
                    icon={Target}
                    color="primary"
                />
                <SummaryCard
                    title={t('reports.overdue', 'Quá hạn')}
                    value={summary?.overdue || 0}
                    subtitle="tasks"
                    icon={AlertTriangle}
                    color={summary?.overdue && summary.overdue > 0 ? 'danger' : 'success'}
                />
                <SummaryCard
                    title={t('reports.vsLastPeriod', 'So với kỳ trước')}
                    value={`${changeVsPrevious?.change_percent || 0}%`}
                    subtitle={`${changeVsPrevious?.completed_previous || 0} → ${summary?.completed || 0}`}
                    icon={changeVsPrevious?.trend === 'up' ? TrendingUp : changeVsPrevious?.trend === 'down' ? TrendingDown : Minus}
                    color={changeVsPrevious?.trend === 'up' ? 'success' : changeVsPrevious?.trend === 'down' ? 'danger' : 'neutral'}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Progress Chart */}
                <div className="card p-6">
                    <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <CalendarDays className="w-5 h-5 text-primary-600" />
                        {t('reports.dailyProgress', 'Tiến độ hàng ngày')}
                    </h2>

                    <div className="space-y-3">
                        {report?.daily_breakdown.map((day) => {
                            const maxValue = Math.max(
                                ...report.daily_breakdown.map(d => Math.max(d.completed, d.created)),
                                1
                            );
                            const completedWidth = (day.completed / maxValue) * 100;

                            const dayDate = new Date(day.date);
                            const dayLabel = dayDate.toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit' });

                            return (
                                <div key={day.date} className="flex items-center gap-3">
                                    <span className="text-xs text-neutral-500 w-16">{dayLabel}</span>
                                    <div className="flex-1 bg-neutral-100 dark:bg-neutral-700 rounded-full h-6 overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full flex items-center justify-end pr-2 transition-all"
                                            style={{ width: `${completedWidth}%` }}
                                        >
                                            {day.completed > 0 && (
                                                <span className="text-xs text-white font-medium">{day.completed}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* By Key Result */}
                <div className="card p-6">
                    <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <Target className="w-5 h-5 text-secondary-600" />
                        {t('reports.byKeyResult', 'Theo Key Result')}
                    </h2>

                    {!report?.by_key_result?.length ? (
                        <div className="text-center py-8 text-neutral-500">
                            <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>{t('reports.noData', 'Chưa có dữ liệu')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {report.by_key_result.slice(0, 6).map((kr) => (
                                <div
                                    key={kr.kr_id}
                                    className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{kr.kr_title}</p>
                                            <p className="text-xs text-neutral-500 truncate">{kr.objective_title}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-sm font-semibold">
                                                {kr.completed_in_period}/{kr.total_tasks}
                                            </span>
                                            {kr.overdue > 0 && (
                                                <p className="text-xs text-danger-600">{kr.overdue} quá hạn</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2 progress-bar">
                                        <div
                                            className="progress-bar-fill"
                                            style={{
                                                width: `${kr.total_tasks > 0 ? (kr.completed_in_period / kr.total_tasks) * 100 : 0}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Focus Recommendation */}
            {report?.focus_recommendation && (
                <div className="card p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-xl">
                            <Lightbulb className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                                {t('reports.focusRecommendation', 'Gợi ý tập trung')}
                            </h3>
                            <p className="text-amber-700 dark:text-amber-300 mt-1">
                                Bạn nên tập trung vào <strong>"{report.focus_recommendation.area}"</strong>
                            </p>
                            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                                💡 {report.focus_recommendation.reason}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Priority Breakdown */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4">Phân bổ theo độ ưu tiên</h2>
                <div className="flex gap-4 flex-wrap">
                    {[
                        { key: 'urgent', label: 'Khẩn cấp', color: 'bg-danger-500' },
                        { key: 'high', label: 'Cao', color: 'bg-warning-500' },
                        { key: 'medium', label: 'Trung bình', color: 'bg-primary-500' },
                        { key: 'low', label: 'Thấp', color: 'bg-neutral-400' },
                    ].map((p) => (
                        <div key={p.key} className="flex items-center gap-2">
                            <div className={clsx('w-3 h-3 rounded-full', p.color)} />
                            <span className="text-sm">{p.label}:</span>
                            <span className="font-semibold">
                                {summary?.by_priority?.[p.key as keyof typeof summary.by_priority] || 0}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
}: {
    title: string;
    value: string | number;
    subtitle: string;
    icon: any;
    color: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
}) {
    const colorClasses = {
        primary: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        success: 'bg-success-50 text-success-600 dark:bg-success-600/20',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
        danger: 'bg-danger-50 text-danger-600 dark:bg-danger-600/20',
        neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700',
    };

    return (
        <div className="card p-4">
            <div className="flex items-center gap-3">
                <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-xs text-neutral-500">{subtitle}</p>
                </div>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">{title}</p>
        </div>
    );
}
